import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * US state codes — 50 states, no territories per D9.
 *
 * ORDERED BY CODE, ascending. This used to run as a geography sweep
 * (NE → SE → MW → SW → W), which groups neighbours but gives a reader hunting
 * for one state nothing to scan by: seven columns of codes in no order they
 * know means reading all 50. Alphabetical is guessable — you land near TX by
 * aiming at the bottom third — and the search box still covers the case where
 * you would rather type it.
 *
 * The sort is applied here rather than trusted to the literal below, so a state
 * appended to the end later cannot quietly break the order.
 */
export const US_STATES: ReadonlyArray<{ code: string; name: string }> =
  [
    { code: 'AK', name: 'Alaska' }, { code: 'AL', name: 'Alabama' }, { code: 'AR', name: 'Arkansas' },
    { code: 'AZ', name: 'Arizona' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'IA', name: 'Iowa' },
    { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'MA', name: 'Massachusetts' }, { code: 'MD', name: 'Maryland' }, { code: 'ME', name: 'Maine' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MO', name: 'Missouri' },
    { code: 'MS', name: 'Mississippi' }, { code: 'MT', name: 'Montana' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'NE', name: 'Nebraska' }, { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NV', name: 'Nevada' },
    { code: 'NY', name: 'New York' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VA', name: 'Virginia' },
    { code: 'VT', name: 'Vermont' }, { code: 'WA', name: 'Washington' }, { code: 'WI', name: 'Wisconsin' },
    { code: 'WV', name: 'West Virginia' }, { code: 'WY', name: 'Wyoming' },
  ].sort((a, b) => a.code.localeCompare(b.code));

/**
 * The State Availability control, shared by /rates, /products and /changes.
 *
 * One 7-column grid of all 50 states with a search that matches on either the
 * code or the full name ("fl" and "florida" both find FL). Every state is
 * always present, so the control reads as a map of the country rather than as
 * a list whose length moves with the data — that stability is the reason this
 * shape was picked as the standard over the vertical checklist /products used
 * to carry.
 *
 * PRESENTATIONAL ONLY. It holds no filter state and injects no service: each
 * page binds its own selection in and applies the toggle to its own store.
 * That is what lets it sit inside three different rails — /rates wraps it in
 * inline-picker-host's head row and pin star, /products in its own rail-field
 * head, /changes in a rail-section — without any of them leaking into the
 * others.
 *
 * Deliberately no per-state counts. The three pages count different things
 * (rate rows, products, scheduled changes), so a count rendered here would
 * have to mean three things at once.
 */
@Component({
  selector: 'app-state-availability-grid',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './state-availability-grid.component.html',
  styleUrl: './state-availability-grid.component.scss',
})
export class StateAvailabilityGridComponent {
  /** Currently selected codes. Empty means "all states" — no narrowing. */
  readonly selected = input<readonly string[]>([]);

  /** A cell was clicked. The host decides what toggling means for its store. */
  readonly stateToggle = output<string>();

  /*
   * NO INSET INPUT, deliberately. The control owns a fixed 12px left/right
   * inset and every host cancels its own ancestor padding down to the 8 left /
   * 2 right that /rates reaches it through, so the grid renders at exactly one
   * width everywhere. A per-host inset switch here would be a second place for
   * the three pages to disagree, which is the thing this component exists to
   * prevent.
   */

  protected readonly searchText = signal('');
  protected readonly allStates = US_STATES;

  protected readonly filteredStates = computed(() => {
    const q = this.searchText().toLowerCase().trim();
    if (!q) return this.allStates;
    return this.allStates.filter(
      s => s.code.toLowerCase().includes(q) || s.name.toLowerCase().includes(q),
    );
  });

  protected isSelected(code: string): boolean {
    return this.selected().includes(code);
  }

  protected onSearchInput(value: string): void {
    this.searchText.set(value);
  }

  protected onToggle(code: string): void {
    this.stateToggle.emit(code);
  }
}
